/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { TextFieldProps } from '@mui/material';
import { type DateView, DesktopDatePicker, DesktopDateTimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { prettifySnakeCase } from '@poweralpha/common';
import type { StandardSchemaV1Issue } from '@tanstack/react-form';
import { toZonedTime } from 'date-fns-tz';
import { useCallback, useMemo } from 'react';
import { getCompactTextFieldClassName } from '../../styles';
import { type DateEntryMode, getDate } from '../../utils';
import InputKeyValue from '../display';
import { FormatValue } from '../FormatValue';
import { useFieldContext } from './formContext';

const getPickerFormat = () => 'MM/dd/yy';
const getPickerViews = (mode?: DateEntryMode): readonly DateView[] =>
  mode ? ['year', 'month'] : ['year', 'month', 'day'];
const getPickerValue = (value: Date | null, mode?: DateEntryMode) =>
  value && mode ? getDate(value, mode) : value;

export interface FormDateFieldProps {
  mode?: DateEntryMode;
  label?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  callbackBeforeSet?: (newDate: Date | null) => Promise<void>;
  disabled?: boolean;
  disableErrorText?: boolean;
  maxDateTime?: Date;
  minDateTime?: Date;
  size?: TextFieldProps['size'] | 'tiny' | 'xs';
  displayOnly?: boolean;
  externalErrors?: Partial<Record<string, Array<StandardSchemaV1Issue>>>;
  'data-testid'?: string;
  'data-visual-test'?: 'transparent';
}

export function FormDateField({
  mode,
  callbackBeforeSet,
  minDateTime,
  maxDateTime,
  size = 'small',
  displayOnly,
  disableErrorText,
  externalErrors,
  ...rest
}: Readonly<FormDateFieldProps>) {
  const field = useFieldContext<Date | undefined | null>();
  const dateValue = useMemo(() => {
    if (!field.state.value) return null;
    const normalizedValue =
      typeof field.state.value === 'string'
        ? toZonedTime(new Date(field.state.value), 'UTC')
        : field.state.value;
    return getPickerValue(normalizedValue, mode);
  }, [field.state.value, mode]);
  const error = useMemo(
    () =>
      (field.state.meta.errors[0]?.message as string | undefined) ??
      externalErrors?.[field.name]?.[0]?.message,
    [field.state.meta.errors, externalErrors, field.name],
  );

  const onChange = useCallback(
    (date: Date | null) => {
      if (date === null) {
        field.handleChange(null);
        return;
      }
      const newDate = getDate(date, mode);

      if (callbackBeforeSet) {
        callbackBeforeSet(newDate).then(() => field.handleChange(newDate));
        return;
      }
      field.handleChange(newDate);
    },
    [callbackBeforeSet, field, mode],
  );

  const commonProps = useMemo(() => {
    const textFieldSize = size === 'tiny' || size === 'xs' ? 'small' : size;

    return {
      label: rest.label ?? prettifySnakeCase(field.name.split('.').at(-1)),
      disablePast: rest.disablePast,
      disableFuture: rest.disableFuture,
      disabled: rest.disabled,
      value: dateValue,
      onChange,
      slotProps: {
        textField: {
          error: Boolean(error),
          helperText: disableErrorText ? undefined : error,
          'data-testid': rest['data-testid'] ?? field.name.split('.').at(-1),
          size: size !== 'tiny' && size !== 'xs' ? size : 'small',
          fullWidth: true,
          className: getCompactTextFieldClassName(size),
        },
        openPickerButton: {
          size: textFieldSize,
        },
      },
    };
  }, [
    rest['data-testid'],
    dateValue,
    error,
    field.name,
    onChange,
    rest.label,
    rest.disablePast,
    rest.disableFuture,
    rest.disabled,
    size,
    disableErrorText,
  ]);

  return useMemo(
    () =>
      displayOnly ? (
        <InputKeyValue
          value={<FormatValue value={commonProps.value} format="date" />}
          label={commonProps.label}
          sx={{ pt: 0, pb: 0 }}
        />
      ) : (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          data-visual-test={rest['data-visual-test']}
        >
          {mode === 'dateTime' ? (
            <DesktopDateTimePicker
              {...commonProps}
              minDateTime={minDateTime}
              maxDateTime={maxDateTime}
              format="MM/dd/yyyy hh:mm a"
            />
          ) : (
            <DesktopDatePicker
              {...commonProps}
              minDate={minDateTime}
              maxDate={maxDateTime}
              views={getPickerViews(mode)}
              format={getPickerFormat()}
            />
          )}
        </LocalizationProvider>
      ),
    [mode, commonProps, minDateTime, maxDateTime, rest['data-visual-test'], displayOnly],
  );
}
